import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { toast } from "sonner"
import { ChevronDown, ChevronRight, Settings, Pencil } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/cn"

type Props = {
  skill: Record<string, unknown>
  /** When set, the description section becomes click-to-edit. */
  skillId?: number
}

function VersionNameField({
  versionName,
  skillId,
  versionId,
}: {
  versionName: string
  skillId?: number
  versionId?: number | null
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState(versionName)
  const [saving, setSaving]   = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!editing) setDraft(versionName)
  }, [versionName, editing])

  useEffect(() => {
    if (!editing) return
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [editing])

  const editable = skillId != null && versionId != null

  if (!editable && !versionName) return null

  if (!editable) {
    return <p className="text-base font-semibold text-app-text">{versionName}</p>
  }

  function startEdit() {
    setDraft(versionName)
    setEditing(true)
  }

  function cancel() {
    setDraft(versionName)
    setEditing(false)
  }

  async function save() {
    const next = draft.trim()
    if (next === (versionName ?? "").trim()) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
      const res = await fetch(`/skills/${skillId}/versions/${versionId}/name`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token":     token,
          "Content-Type":     "application/x-www-form-urlencoded",
          "Accept":           "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: new URLSearchParams({ name: next }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.error ?? `Could not save (${res.status}).`)
        return
      }
      toast.success(next ? "Version name saved." : "Version name cleared.")
      setEditing(false)
      router.reload({ only: ["skill"] })
    } catch {
      toast.error("Could not save version name.")
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault()
              cancel()
            } else if (e.key === "Enter") {
              e.preventDefault()
              save()
            }
          }}
          maxLength={120}
          placeholder="Name this version (e.g. milestone-fix backport)"
          disabled={saving}
          className="flex-1 rounded-control border border-border bg-app-bg px-3 py-2 text-base font-semibold text-app-text outline-none focus:border-brand-red"
        />
        <Button type="button" size="sm" variant="ghost" onClick={cancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" size="sm" variant="red" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      className="group flex w-full items-center gap-2 rounded border border-transparent px-2 py-1 text-left transition hover:border-border hover:bg-white/[0.03]"
      title="Click to edit"
    >
      <span className={cn("flex-1 truncate text-base font-semibold", versionName ? "text-app-text" : "text-app-subtle italic")}>
        {versionName || "Click to name this version"}
      </span>
      <Pencil size={12} strokeWidth={2} className="shrink-0 text-app-subtle opacity-0 transition group-hover:opacity-100" />
    </button>
  )
}

function DescriptionField({ description, skillId }: { description: string; skillId?: number }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState(description)
  const [saving, setSaving]   = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Resync the local draft when the persisted description changes
  // (e.g. after Inertia reloads the skill prop on Apply / on a sibling
  // edit). Only when not actively editing — don't clobber an in-progress
  // edit if the operator's mid-keystroke.
  useEffect(() => {
    if (!editing) setDraft(description)
  }, [description, editing])

  useEffect(() => {
    if (!editing) return
    textareaRef.current?.focus()
    textareaRef.current?.select()
  }, [editing])

  if (skillId == null) {
    return <p className="whitespace-pre-wrap text-app-text">{description}</p>
  }

  function startEdit() {
    setDraft(description)
    setEditing(true)
  }

  function cancel() {
    setDraft(description)
    setEditing(false)
  }

  async function save() {
    const next = draft.trim()
    if (!next) {
      toast.error("Description can't be blank.")
      return
    }
    if (next === description.trim()) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
      const res = await fetch(`/skills/${skillId}/description`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token":     token,
          "Content-Type":     "application/x-www-form-urlencoded",
          "Accept":           "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: new URLSearchParams({ description: next }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.error ?? `Could not save (${res.status}).`)
        return
      }
      toast.success("Description saved.")
      setEditing(false)
      router.reload({ only: ["skill"] })
    } catch {
      toast.error("Could not save description.")
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <Textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault()
              cancel()
            } else if ((e.key === "Enter") && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              save()
            }
          }}
          rows={Math.min(10, Math.max(3, Math.ceil((draft.length || 1) / 60)))}
          disabled={saving}
        />
        <div className="flex items-center justify-end gap-2">
          <span className="mr-auto text-[10px] text-app-subtle">⌘/Ctrl + Enter to save · Esc to cancel</span>
          <Button type="button" size="sm" variant="ghost" onClick={cancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" size="sm" variant="red" onClick={save} disabled={saving || !draft.trim()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      className="group flex w-full items-start gap-2 rounded border border-transparent px-2 py-1 text-left transition hover:border-border hover:bg-white/[0.03]"
      title="Click to edit"
    >
      <span className={cn("flex-1 whitespace-pre-wrap", description ? "text-app-text" : "text-app-subtle italic")}>
        {description || "Click to add a description"}
      </span>
      <Pencil size={12} strokeWidth={2} className="mt-0.5 shrink-0 text-app-subtle opacity-0 transition group-hover:opacity-100" />
    </button>
  )
}

function Section({ label, count, children }: { label: string; count?: number | string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline gap-2 text-[10px] uppercase tracking-wide text-app-subtle">
        <span>{label}</span>
        {count !== undefined && <span className="text-app-subtle">{count}</span>}
      </div>
      {children}
    </div>
  )
}

function Collapsible({
  label, count, defaultOpen, children,
}: { label: string; count?: number | string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen ?? false)
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 text-left"
      >
        {open ? <ChevronDown size={12} strokeWidth={2} /> : <ChevronRight size={12} strokeWidth={2} />}
        <span className="text-[10px] uppercase tracking-wide text-app-subtle">{label}</span>
        {count !== undefined && (
          <span className={cn("text-[10px] text-app-subtle", !open && "ml-auto")}>{count}</span>
        )}
      </button>
      {open && <div className="mt-1.5">{children}</div>}
    </div>
  )
}

// Body-only version of the setup view. No Card wrapper, no header —
// suitable for embedding inside a tabbed container (DesignerPane uses
// this inside a Setup/Pending tab strip). Use SkillSetupPanel below
// for the standalone Card-wrapped variant.
export function SkillSetupBody({ skill, skillId }: Props) {
  const description       = (skill.description as string) ?? ""
  const workflowYaml      = (skill.workflowYaml as string) ?? ""
  const notes             = (skill.notes as string) ?? ""
  const toolKeys          = (Array.isArray(skill.derivedToolKeys) ? skill.derivedToolKeys as string[] : [])
  const versionName       = (skill.versionName as string) ?? ""
  const versionId         = (skill.versionId as number | null | undefined) ?? null

  return (
    <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3 text-sm">
      {(versionName || skillId != null) && (
        <Section label="Version name">
          <VersionNameField versionName={versionName} skillId={skillId} versionId={versionId} />
        </Section>
      )}

      {(description || skillId != null) && (
        <Section label="Description">
          <DescriptionField description={description} skillId={skillId} />
        </Section>
      )}

      <Collapsible label="Workflow YAML" defaultOpen={true} count={workflowYaml ? `${workflowYaml.length} chars` : "empty"}>
        {workflowYaml
          ? <pre className="rounded bg-app-bg p-3 text-[12px] whitespace-pre-wrap text-app-text">{workflowYaml}</pre>
          : <p className="text-app-subtle">(empty — Forge can author the workflow)</p>}
      </Collapsible>

      <Section label="Derived tools" count={toolKeys.length}>
        {toolKeys.length === 0 ? (
          <span className="text-app-subtle">no tools wired</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {toolKeys.map((k) => (
              <code key={k} className="rounded-chip border border-border bg-white/5 px-2 py-0.5 text-[11px] text-app-text">
                {k}
              </code>
            ))}
          </div>
        )}
      </Section>

      {notes && (
        <Collapsible label="Notes">
          <p className="whitespace-pre-wrap text-app-subtle text-xs">{notes}</p>
        </Collapsible>
      )}
    </div>
  )
}

// Standalone Card-wrapped variant. Used wherever we want the setup view
// to stand on its own (not as a tab body).
export function SkillSetupPanel({ skill, skillId }: Props) {
  const version = skill.version
  const state   = (skill.state as string) ?? ""
  const stateTone: "gold" | "red" | "neutral" = state === "live" ? "gold" : state === "draft" ? "red" : "neutral"

  return (
    <Card className="flex h-full min-h-0 flex-col p-0">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Settings size={14} strokeWidth={1.7} className="text-app-muted" />
          <div>
            <h3 className="text-sm font-semibold">Current setup</h3>
            <p className="text-[10px] text-app-subtle">Persisted skill state</p>
          </div>
        </div>
        <Badge tone={stateTone}>v{String(version)} {state}</Badge>
      </header>
      <SkillSetupBody skill={skill} skillId={skillId} />
    </Card>
  )
}
